import { type Achievements, type GameSettings, defaultAchievements, defaultSettings } from "../core/model";
import { isLanguageSetting } from "../core/language";
import {
  type RankingEntry,
  createDefaultRanking,
  exportLegacyIniRanking,
  normalizeRanking,
  parseLegacyIniRanking,
  sanitizeName
} from "../core/ranking";

const RANKING_KEY = "friel.ranking.v1";
const SETTINGS_KEY = "friel.settings.v1";
const ACHIEVEMENTS_KEY = "friel.achievements.v1";
const LAST_NAME_KEY = "friel.lastName.v1";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

export function browserStorage(): StorageLike | null {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  return window.localStorage;
}

export function loadRanking(storage: StorageLike | null = browserStorage()): RankingEntry[] {
  if (!storage) {
    return createDefaultRanking();
  }

  try {
    const raw = storage.getItem(RANKING_KEY);
    if (!raw) {
      return createDefaultRanking();
    }
    const parsed = JSON.parse(raw) as RankingEntry[];
    return normalizeRanking(parsed);
  } catch {
    return createDefaultRanking();
  }
}

export function saveRanking(ranking: RankingEntry[], storage: StorageLike | null = browserStorage()): void {
  if (!storage) {
    return;
  }
  storage.setItem(RANKING_KEY, JSON.stringify(normalizeRanking(ranking)));
}

export function importLegacyRanking(text: string, storage: StorageLike | null = browserStorage()): RankingEntry[] {
  const ranking = parseLegacyIniRanking(text);
  saveRanking(ranking, storage);
  return ranking;
}

export function exportLegacyRanking(ranking: RankingEntry[]): string {
  return exportLegacyIniRanking(ranking);
}

export function loadLastName(storage: StorageLike | null = browserStorage()): string {
  if (!storage) {
    return "";
  }

  try {
    return normalizeStoredName(storage.getItem(LAST_NAME_KEY) ?? "");
  } catch {
    return "";
  }
}

export function saveLastName(name: string, storage: StorageLike | null = browserStorage()): void {
  if (!storage) {
    return;
  }
  storage.setItem(LAST_NAME_KEY, normalizeStoredName(name));
}

export function loadSettings(storage: StorageLike | null = browserStorage()): GameSettings {
  const defaults = defaultSettings();
  if (!storage) {
    return defaults;
  }

  try {
    const raw = storage.getItem(SETTINGS_KEY);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw) as Partial<GameSettings>;
    return normalizeSettings({ ...defaults, ...parsed });
  } catch {
    return defaults;
  }
}

export function saveSettings(settings: GameSettings, storage: StorageLike | null = browserStorage()): void {
  if (!storage) {
    return;
  }
  storage.setItem(SETTINGS_KEY, JSON.stringify(normalizeSettings(settings)));
}

export function loadAchievements(storage: StorageLike | null = browserStorage()): Achievements {
  const defaults = defaultAchievements();
  if (!storage) {
    return defaults;
  }

  try {
    const raw = storage.getItem(ACHIEVEMENTS_KEY);
    if (!raw) {
      return defaults;
    }
    return normalizeAchievements({ ...defaults, ...(JSON.parse(raw) as Partial<Achievements>) });
  } catch {
    return defaults;
  }
}

export function saveAchievements(achievements: Achievements, storage: StorageLike | null = browserStorage()): void {
  if (!storage) {
    return;
  }
  storage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(normalizeAchievements(achievements)));
}

function normalizeSettings(settings: GameSettings): GameSettings {
  return {
    bgm: Boolean(settings.bgm),
    se: Boolean(settings.se),
    bgmVolume: normalizeVolume(settings.bgmVolume),
    seVolume: normalizeVolume(settings.seVolume),
    touchControls: Boolean(settings.touchControls),
    language: isLanguageSetting(settings.language) ? settings.language : "auto"
  };
}

function normalizeVolume(value: number): number {
  if (!Number.isFinite(value)) {
    return 70;
  }
  return Math.max(0, Math.min(100, Math.round(value / 10) * 10));
}

function normalizeAchievements(achievements: Achievements): Achievements {
  return {
    highScore: Math.max(0, Math.floor(achievements.highScore || 0)),
    maxLevel: Math.max(1, Math.floor(achievements.maxLevel || 1)),
    maxChain: Math.max(0, Math.floor(achievements.maxChain || 0)),
    maxSimultaneousErase: Math.max(0, Math.floor(achievements.maxSimultaneousErase || 0))
  };
}

function normalizeStoredName(name: string): string {
  return sanitizeName(name);
}

export class MemoryStorage implements StorageLike {
  private readonly values = new Map<string, string>();

  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }

  removeItem(key: string): void {
    this.values.delete(key);
  }
}
